import React, { useContext as useContextCore, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useLocalStorage } from 'react-use'
import { useList } from '@ztechapps/common.hook'
import type { UseTaskFilterStateResult } from '@ztechapps/task.common'
import { useTaskFilterState } from '@ztechapps/task.common'
import type { UseTaskViewStateResult } from '@ztechapps/task.common'
import { useTaskViewState } from '@ztechapps/task.common'
import { TaskBoardGroupByEnum } from '@ztechapps/task.common'
import type { UseListResult } from '@ztechapps/common.hook'

/**
 * Lightweight project fragment for filter bar.
 * Includes relatedWorkOrders for work-order scoping.
 */
const taskFilterProjectFragment = gql`
  fragment taskFilterProjectFragment on Project {
    _id
    code
    name
    status
    relatedWorkOrders {
      _id
    }
  }
`

const taskFilterProjectListQuery = gql`
  query allProject($filter: JSON!, $options: JSON) {
    allProject(filter: $filter, options: $options) {
      ...taskFilterProjectFragment
    }
  }
  ${taskFilterProjectFragment}
`

/**
 * Single query that accepts a base filter and returns all status counts.
 * The server adds status conditions internally — no need to send 5 filters.
 */
const taskStatusCountsQuery = gql`
  query getTaskStatusCounts($filter: JSON) {
    getTaskStatusCounts(filter: $filter) {
      all
      draft
      inProgress
      complete
      cancelled
    }
  }
`

type ProjectForFilter = {
  _id: string
  code: string
  name: string
  status: string
  relatedWorkOrders: { _id: string }[]
}

export type StatusCounts = {
  all: number
  draft: number
  inProgress: number
  complete: number
  cancelled: number
}

export type TaskFilterBarContextType = UseTaskFilterStateResult &
  UseTaskViewStateResult & {
    projectList: UseListResult<ProjectForFilter>
    statusCounts: StatusCounts
    statusCountsLoading: boolean
    groupBy: TaskBoardGroupByEnum
    setGroupBy: (value: TaskBoardGroupByEnum) => void
  }

const context = React.createContext<TaskFilterBarContextType>({} as TaskFilterBarContextType)

export type TaskFilterBarProviderProps = {
  children: React.ReactNode
}

type StatusCountsQueryResult = {
  getTaskStatusCounts: StatusCounts
}

const useStatusCounts = (nonStatusFilter: Record<string, any>) => {
  const result = useQuery<StatusCountsQueryResult>(taskStatusCountsQuery, {
    variables: { filter: nonStatusFilter },
    fetchPolicy: 'cache-and-network',
  })

  const statusCounts = useMemo<StatusCounts>(
    () => ({
      all: result.data?.getTaskStatusCounts?.all ?? 0,
      draft: result.data?.getTaskStatusCounts?.draft ?? 0,
      inProgress: result.data?.getTaskStatusCounts?.inProgress ?? 0,
      complete: result.data?.getTaskStatusCounts?.complete ?? 0,
      cancelled: result.data?.getTaskStatusCounts?.cancelled ?? 0,
    }),
    [result.data]
  )

  return { statusCounts, loading: result.loading }
}

export const TaskFilterBarProvider = ({ children }: TaskFilterBarProviderProps) => {
  const filterState = useTaskFilterState()
  const viewState = useTaskViewState()
  const [groupByStorage, setGroupBy] = useLocalStorage<TaskBoardGroupByEnum>(
    'task-board-group-by',
    TaskBoardGroupByEnum.None
  )
  const groupBy = groupByStorage ?? TaskBoardGroupByEnum.None

  const projectList = useList<ProjectForFilter>({
    query: taskFilterProjectListQuery,
    sort: { createdAt: -1 },
    limit: 200,
    options: {
      queryOptions: {
        fetchPolicy: 'cache-and-network',
      },
    },
  })

  const { statusCounts, loading: statusCountsLoading } = useStatusCounts(filterState.nonStatusFilter)

  const value = useMemo<TaskFilterBarContextType>(
    () => ({
      ...filterState,
      ...viewState,
      projectList,
      statusCounts,
      statusCountsLoading,
      groupBy,
      setGroupBy,
    }),
    [filterState, viewState, projectList, statusCounts, statusCountsLoading, groupBy, setGroupBy]
  )

  return <context.Provider value={value}>{children}</context.Provider>
}

export const useTaskFilterBarContext = () => useContextCore(context)
